import moment, { Moment } from "moment-timezone";

import { DEFAULT_TIMEZONE } from "../../constants";
import GLOBAL_DATA from "../../../global-data.json";

const CONFERENCE_SCHEDULE_TOURNAMENTS = ["NCAAT", "NIT", "nit-tournament", "ncaa-tournament"];
// Postseason conferences that are only listed once the NCAA Tournament field is announced
const POSTSEASON_CONFERENCES = ["NCAAT", "NIT", "CBI", "TBC"];
const SCHEDULE_LEAGUES: Record<string, string> = {
  nfl: "NFL",
  ncaaf: "CFB",
  mls: "MLS",
  wc: "WC",
  deb: "DEB",
  epl: "EPL",
  esp: "ESP",
  itsa: "ITSA",
  ucl: "UCL",
};

interface PrefetchedScheduleItem {
  conference: string | null;
  roundName: string;
  startDate: string;
  endDate: string;
  skip: boolean;
  order: number;
}

interface PrefetchedLeague {
  name: string;
  schedule: PrefetchedScheduleItem[];
}

export interface ScheduleItem {
  name: string;
  startDate: string;
  endDate: string;
  skip?: boolean;
}

const getEasternDay = (date: string) => moment.tz(moment.utc(date).format("YYYY-MM-DD"), DEFAULT_TIMEZONE);

/**
 * Schedule entries are Eastern calendar dates stored at UTC midnight. Weekly schedules end at the next
 * entry's start (exclusive), tournament rounds at 23:59:59 of their last day (inclusive). Returns the
 * query range for an entry as Eastern instants, independent of the browser timezone.
 */
export const getScheduleItemRange = (item: Pick<ScheduleItem, "startDate" | "endDate">) => {
  const end = moment.utc(item.endDate);
  const isExclusiveEnd = end.isSame(end.clone().startOf("day"));
  return {
    after: getEasternDay(item.startDate),
    before: getEasternDay(item.endDate)
      .add(isExclusiveEnd ? 0 : 1, "day")
      .subtract(1, "ms"),
  };
};

/**
 * The API's game days are Eastern calendar days. Returns the query range for the calendar date of `date`
 * (in its own timezone, e.g. the browser's) as an Eastern day, independent of the browser timezone.
 */
export const getGameDayRange = (date: Moment) => {
  const after = moment.tz(date.format("YYYY-MM-DD"), DEFAULT_TIMEZONE);
  return { after, before: after.clone().endOf("day") };
};

const getPrefetchedLeague = (leagueName: string): PrefetchedLeague | undefined =>
  GLOBAL_DATA.leagues.find((league: PrefetchedLeague) => league.name === leagueName);

const normalizeSchedule = (schedule: PrefetchedScheduleItem[]): ScheduleItem[] =>
  schedule
    .slice()
    .sort((first, second) => first.order - second.order)
    .map(({ roundName, startDate, endDate, skip }) => ({
      name: roundName,
      startDate,
      endDate,
      skip,
    }));

/** From the start of the first round to the end of the championship of the latest NCAA Tournament */
const getNcaaTournamentRange = (): { after: Moment; before: Moment } | undefined => {
  const rounds = (getPrefetchedLeague("CBK")?.schedule ?? [])
    .filter((item) => item.conference === "NCAAT")
    .map(getScheduleItemRange);
  if (rounds.length === 0) {
    return undefined;
  }
  const latestYear = Math.max(...rounds.map(({ after }) => after.year()));
  const latestRounds = rounds.filter(({ after }) => after.year() === latestYear);
  return {
    after: moment.min(latestRounds.map(({ after }) => after)),
    before: moment.max(latestRounds.map(({ before }) => before)),
  };
};

const getNcaaTournamentStart = () => getNcaaTournamentRange()?.after;

/** The field is announced on Selection Sunday, the Sunday before the first round, by 7pm Eastern */
const getNcaaSelectionTime = () => getNcaaTournamentStart()?.clone().day(0).hour(19);

/** True from Selection Sunday until the next season's tournament is added to the schedule */
export const hasNcaaTournamentStarted = () => getNcaaSelectionTime()?.isBefore() ?? false;

/** The first round's start, between Selection Sunday and the first round */
export const getUpcomingNcaaTournamentStart = () => {
  const start = getNcaaTournamentStart();
  return hasNcaaTournamentStarted() && start?.isAfter() ? start : undefined;
};

/** True while the NCAA Tournament is being played */
export const isNcaaTournamentInProgress = () => {
  const range = getNcaaTournamentRange();
  return range ? moment().isBetween(range.after, range.before, undefined, "[]") : false;
};

/** The sport's conferences, without the postseason ones before Selection Sunday */
export const getConferenceList = <T extends { key: string }>(conferenceList?: T[]) =>
  hasNcaaTournamentStarted()
    ? conferenceList
    : conferenceList?.filter((conference) => !POSTSEASON_CONFERENCES.includes(conference.key));

export const hasConfiguredConferenceSchedule = (conference?: string | null) =>
  hasNcaaTournamentStarted() && CONFERENCE_SCHEDULE_TOURNAMENTS.includes(conference ?? "");

export const getPrefetchedSchedule = (sportKey: string): ScheduleItem[] => {
  const leagueName = SCHEDULE_LEAGUES[sportKey];
  return normalizeSchedule(getPrefetchedLeague(leagueName)?.schedule ?? []);
};

export const getSchedule = async (sportKey: string): Promise<ScheduleItem[]> => {
  if (SCHEDULE_LEAGUES[sportKey]) {
    return getPrefetchedSchedule(sportKey);
  }

  return [];
};

export const getConferenceSchedule = (conference?: string): ScheduleItem[] | null => {
  if (conference === "NCAAT" || conference === "NIT") {
    const schedule = getPrefetchedLeague("CBK")?.schedule ?? [];
    return normalizeSchedule(schedule.filter((item) => item.conference === conference));
  }

  return null;
};

// Team rankings pages and the tournament whose rounds they list
const TEAM_RANKINGS_CONFERENCES: Record<string, string> = { "ncaa-tournament": "NCAAT" };

export const getTeamRankingsSchedule = (slug: string): ScheduleItem[] =>
  getConferenceSchedule(TEAM_RANKINGS_CONFERENCES[slug]) ?? [];

/** False for unknown team rankings pages and tournaments without rounds in the schedule */
export const hasTeamRankingsSchedule = (slug: string) => getTeamRankingsSchedule(slug).length > 0;
